import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import React, { useMemo, useState, useCallback } from 'react';
import {
  buildStyles,
  CircularProgressbar,
  CircularProgressbarWithChildren,
} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { t } from 'ttag';
import { Button } from '@coolremote/shared';
import { CoolMode, HotMode } from '../../icons/';
import { useStoreActions, useStoreState } from '../../models/RootStore';
import useStyles from './SiteCountCard.style';
import WorkingUnitsTableComponent from './WorkingUnitsTableComponent';

enum UnitMode {
  Cool = 'Cool',
  Heat = 'Heat',
  Other = 'Other',
}
const UNIT_MODE_DETAILS = {
  [UnitMode.Cool]: { name: t`Cool`, color: '#35a8e0' },
  [UnitMode.Heat]: { name: t`Heat`, color: '#ff0000' },
  [UnitMode.Other]: { name: t`Other`, color: '#79797d' },
};
const MODES = Object.values(UnitMode);

const SiteCountCard: React.FC<any> = (props) => {
  const { totalIndoorUnits, conditionIndoorUnits, blueIndoorUnits, redIndoorUnits, title, customerId, siteId, refresh, loading } = props;
  const classes = useStyles();
  const redAndBlueValue = ((redIndoorUnits + blueIndoorUnits) / totalIndoorUnits) * 100;
  const blueValue = (blueIndoorUnits / totalIndoorUnits) * 100;
  const [openWorkingUnits, setOpenWorkingUnits] = useState<any>(null);
  const [workingUnits, setWorkingUnits] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { operationStatusesMirror } = useStoreState((state) => state);
  const { on: powerOnVal, off: powerOffVal } = operationStatusesMirror;
  const unitPowerControl = useStoreActions((state) => state.units.changePowerState);
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const [allWorkingUnits, setAllWorkingUnits] = useState<any>(null);
  const [selectedMode, setSelectedMode] = useState<any>('');
  const [isDirty, setIsDirty] = useState(false);
  const getSiteWorkingUnits = useStoreActions(
    (actions) => actions.sites.getSiteWorkingUnits
  );
  const getCustomerWorkingUnits = useStoreActions(
    (actions) => actions.customers.getCustomerWorkingUnits
  );

  const shouldShowUnits = (mode: string, site: any) => {
    return (mode == selectedMode) && site?.[`onUnits${mode}Count`] > 0;
  };

  const fetchWorkingUnits = async () => {
    let workingUnits;
    setIsLoading(true);
    try {
      if (siteId) {
        workingUnits = await getSiteWorkingUnits(siteId);
        setWorkingUnits({ sites: { [siteId]: workingUnits } });
      } else {
        workingUnits = await getCustomerWorkingUnits(customerId);
        setWorkingUnits(workingUnits);
      }
    } catch (error: any) {
      addMessage({ message: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleWorkingUnits = async (unitsCount: number, mode: any) => {
    if (unitsCount > 0) {
      setOpenWorkingUnits(mode);
      setSelectedMode(mode);
      setAllWorkingUnits(mode === 'ALL' ? 'open' : '');
      await fetchWorkingUnits();
    }
  };

  const handleClose = () => {
    setOpenWorkingUnits(null);
    setAllWorkingUnits(null);
    setSelectedMode('');
    if (isDirty) {
      setIsDirty(false);
      refresh();
    }
  };

  const toggleUnitPower = async (
    event: any,
    unitId: string,
    newIsItemOn: boolean
  ) => {
    event.stopPropagation();
    event.preventDefault();
    const changeStatusTo: number = newIsItemOn ? +powerOnVal : +powerOffVal;

    try {
      await unitPowerControl({ unitId, state: changeStatusTo });
      setIsDirty(true);
      // Update the `isItemOn` state of the specific unit
      setWorkingUnits((prevUnits: any) => {
        const updatedUnits = { ...prevUnits };
        Object.keys(updatedUnits.sites).forEach((siteId) => {
          MODES.forEach((mode) => {
            const key = `onUnits${mode}Arr`;
            updatedUnits.sites[siteId][key] = updatedUnits.sites[siteId][key]
            .map((unit: any) => {
              if (unit.id === unitId) {
                return { ...unit, isItemOn: newIsItemOn };
              }
              return unit;
            });
          });
        });
        return updatedUnits;
      });
    } catch (error: any) {
      addMessage({ message: error.message });
    }
  };

  const tableData = useMemo(() => {
    if (!workingUnits || !workingUnits.sites) {
      return [];
    }
    return Object.entries(workingUnits.sites).map(([siteId, site]) => {
      const patchedSite: Record<string, any> =
        typeof site === 'object' && site !== null ? { ...site } : {};
      MODES.forEach((mode) => {
        const key = `onUnits${mode}Arr`;
        patchedSite[key] = patchedSite[key].map((unit: any) => ({
          ...unit,
          isItemOn: unit.isItemOn !== undefined ? unit.isItemOn : true,
        }));
      });
      return [siteId, patchedSite];
    });
  }, [workingUnits]);

  const renderUnitInfo = useCallback((count: number, mode: UnitMode) => (
    <div key={`mode-${mode}`} className={classes.headerInfoSection}>
      <Typography className={classes.headerInfoVal}>{count}</Typography>
      <Typography
        className={classes.headerInfoTitle}
        style={{ color: UNIT_MODE_DETAILS[mode].color }}
      >
        {UNIT_MODE_DETAILS[mode].name}
      </Typography>
    </div>
  ), [classes]);

  const renderSiteInfo = useCallback((site: any) => {
    const showHeatUnits = shouldShowUnits(UnitMode.Heat, site);
    const showCoolUnits = shouldShowUnits(UnitMode.Cool, site);

    if (!((allWorkingUnits && site?.onUnitsCount) || showHeatUnits || showCoolUnits)) {
      return null;
    }

    return (
      <div key={`site-info-${site?.id}`} className={classes.reportsHeader}>
        <Typography className={classes.siteName}>{site?.name}</Typography>
        <div className={classes.headerInfoContainer}>
          {MODES.map((mode) => {
            const shouldShow = allWorkingUnits || shouldShowUnits(mode, site);
            const countKey = `onUnits${mode}Count`;
            return shouldShow && renderUnitInfo(site?.[countKey], mode);
          })}
        </div>
      </div>
    );
  }, [allWorkingUnits, shouldShowUnits, renderUnitInfo, classes]);

  const renderContent = () => {
    if (loading) {
      return <div className={classes.loaderHolder}><CircularProgress /></div>;
    }
    return (
    <>
  <div className={classes.row}>
          <div
            id="column1"
            className={classes.column1Holder}
            onClick={() => handleWorkingUnits(conditionIndoorUnits, 'ALL')}
          >
            <div className={classes.relativeContainer}>
              <Typography className={classes.onIndoorCountStyle}>
                {Math.round(conditionIndoorUnits)}
              </Typography>
              <Typography className={classes.indoorUnitsCountStyle}>
                {Math.round(totalIndoorUnits)}
              </Typography>
              <CircularProgressbarWithChildren
                value={totalIndoorUnits !== 0 ? redAndBlueValue : 0}
                strokeWidth={4}
                styles={buildStyles({
                  pathColor: '#f00',
                  trailColor: '#eee',
                })}
              >
                <CircularProgressbar
                  value={totalIndoorUnits !== 0 ? blueValue : 0}
                  strokeWidth={4}
                  styles={buildStyles({
                    trailColor: 'transparent',
                  })}
                />
              </CircularProgressbarWithChildren>
            </div>
          </div>
          <div id="column2" className={classes.column2Holder}>
            <Typography
              className={classes.modesCount}
              onClick={() => handleWorkingUnits(blueIndoorUnits, UnitMode.Cool)}
            >
              <CoolMode className={classes.modesIcons} /> {blueIndoorUnits}{' '}
              {t`Units`}
            </Typography>
            <Typography
              className={classes.modesCount}
              onClick={() => handleWorkingUnits(redIndoorUnits, UnitMode.Heat)}
            >
              <HotMode className={classes.modesIcons} /> {redIndoorUnits}{' '}
              {t`Units`}
            </Typography>
          </div>
        </div>
        <div className={classes.row}>
          <Typography className={classes.indoorUnitsText}>{title}</Typography>
        </div>
    </>
    );
  };

  return (
    <div id="site" className={classes.siteCard}>
      <div className={classes.container}>
        <div className={classes.cardHeader}>
          <Typography className={classes.cardTitle}>{t`Units ON`}</Typography>
        </div>
        {renderContent()}
      </div>

      {openWorkingUnits && (
        <Dialog
          onClose={handleClose}
          aria-labelledby="working-units-dialog"
          open={true}
          fullScreen={true}
          classes={{ paper: classes.dialogPaper }}
        >
          <DialogTitle
            className={classes.workingUnitsTitle}
            id="customized-dialog-title"
          >
            {openWorkingUnits + t` working units`}
          </DialogTitle>
          <DialogContent>
            <div className={classes.dialogContent}>
              {isLoading ? (
                <div className={classes.loaderHolder}>
                  <CircularProgress />
                </div>
              ) : (
                <>
                  {tableData.map((sites: any, index: any) =>
                    sites?.map((site: any) => {
                      if (!site.name) {
                        return null;
                      }
                      return (
                        <div key={index}>
                          {renderSiteInfo(site)}
                          {allWorkingUnits ? (
                            MODES.map((mode: any, i: any) => (
                              <WorkingUnitsTableComponent
                                key={i}
                                site={site}
                                mode={mode}
                                toggleUnitPower={toggleUnitPower}
                              />
                            ))
                          ) : (
                            <WorkingUnitsTableComponent
                              site={site}
                              mode={selectedMode}
                              toggleUnitPower={toggleUnitPower}
                            />
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={handleClose} color="primary">
              {t`Close`}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default SiteCountCard;
